import { type RefObject, useEffect } from 'react'
import { computeRevealOffset, computeOverflow } from './followSticky.js'

/** Het event waarmee iets bovenaan dat plakt (de compacte kop) zegt dat de lucht erboven veranderde. */
export const STICKY_TOP_CHANGED = 'ef-plaktop-gewijzigd'

export interface FollowStickyOptions {
  /**
   * Lucht boven het gepinde element, en tegelijk de drempel waarop het pinnen
   * begint. Standaard 16px, gelijk aan de `top: var(--nwo-space-sp-4)` die
   * `.ef-inhoudsopgave` in schil.css al heeft: die CSS-waarde is de rustpositie
   * vóór de eerste scrollstap, deze hook neemt het daarna over.
   */
  readonly topGap?: number | (() => number)
  /**
   * Lucht onder het element zodra zijn onderkant volledig is onthuld. Een functie als de ruimte
   * pas bij het meten bekend is, zoals de hoogte van de actiebalk die onderaan plakt: die wordt
   * bij elke stap opnieuw gelezen.
   */
  readonly bottomGap?: number | (() => number)
}

/**
 * "Leesrichting-volgende sticky": houdt een sticky element (hier: de inhoudsopgave,
 * `Inhoudsopgave.tsx`) in beeld door het met de pagina mee te bewegen en, is het
 * langer dan het scherm, zijn onderkant te onthullen bij naar beneden scrollen en
 * zijn bovenkant zodra je omkeert. Overgenomen van CitatenPOC's `useFollowSticky`
 * (`src/editor/useFollowSticky.ts`): eFormulieren/CLAUDE.md noemt die POC de
 * maatstaf voor het gedrag van controls op het formulier, niet één van meerdere
 * opties. Plain CSS `position: sticky` kan maar aan één rand pinnen en zou de
 * onderkant van een lange inhoudsopgave (veel secties, een smal scherm) dus nooit
 * bereikbaar maken; deze hook verschuift daarom de sticky-`top` afhankelijk van de
 * scrollrichting.
 *
 * Bewust GEEN `transform`: een naar beneden verschoven element in de flow vergroot
 * de scrollhoogte van de pagina (de onderkant zou dan nooit te bereiken zijn). Een
 * sticky-element blijft binnen zijn containing block en voegt geen scrolruimte toe.
 * De rekenkern (wanneer pinnen begint, hoeveel er te onthullen valt) staat los in
 * `volgSticky.ts`, met een eigen Node-test; hier alleen de DOM-bedrading.
 *
 * Mobiel/smal zet de `@container (max-width: 48rem)`-regel in schil.css
 * `.ef-inhoudsopgave` zelf op `position: static` (R-158: een ingebed formulier
 * reageert op zijn eigen breedte en niet op die van het venster, dus een
 * containerquery en geen media query zoals CitatenPOC's `matchMedia` gebruikt). Deze
 * hook leest daarom de COMPUTED `position` van het element zelf om te bepalen of hij
 * iets moet doen, in plaats van zelf nog eens een breedtedrempel te verzinnen die met
 * die CSS-regel uit de pas kan lopen.
 *
 * `anchorRef` moet een niet-getransformeerd/niet-sticky element zijn
 * (`.ef-formulier__body`) zodat de natuurlijke viewport-positie stabiel te meten is;
 * `elementRef` is de inhoudsopgave zelf. Beide zijn live refs (`.current` telkens
 * opnieuw gelezen, niet één keer bij setup): de inhoudsopgave rendert pas vanaf twee
 * secties (`MINIMUM_SECTIONS` in `Inhoudsopgave.tsx`) en kan dus later mounten, of bij
 * een sectiewijziging weer verdwijnen.
 */
export function useFollowSticky(
  elementRef: RefObject<HTMLElement | null>,
  anchorRef: RefObject<HTMLElement | null>,
  { topGap = 16, bottomGap = 16 }: FollowStickyOptions = {},
): void {
  useEffect(() => {
    let revealOffset = 0
    let previousScrollY = window.scrollY
    let frame = 0
    let waitFrame = 0
    const observer = new ResizeObserver(() => plan())
    let observed: HTMLElement | null = null

    const apply = () => {
      frame = 0
      const element = elementRef.current
      const anchor = anchorRef.current
      if (element === null || anchor === null) return

      // Volg het huidige element voor hoogteveranderingen (inklappen, taalwissel,
      // of een node-wissel doordat de inhoudsopgave opnieuw is gemount).
      if (observed !== element) {
        if (observed !== null) observer.unobserve(observed)
        observer.observe(element)
        observed = element
      }

      const scrollY = window.scrollY
      const delta = scrollY - previousScrollY
      previousScrollY = scrollY

      if (getComputedStyle(element).position !== 'sticky') {
        // De containerquery in schil.css heeft dit element op `position: static`
        // gezet (smal formulier, R-158): dit mechanisme hoort dan niets te doen.
        if (element.style.top !== '') element.style.top = ''
        revealOffset = 0
        return
      }

      // Een functie als de lucht erboven pas bij het meten bekend is, zoals de compacte kop die
      // bovenaan plakt zodra de echte kop uit beeld is (`CompactHeader`): bij elke stap opnieuw gelezen.
      const top = typeof topGap === 'function' ? topGap() : topGap
      const overflow = computeOverflow(element.offsetHeight, window.innerHeight, top, typeof bottomGap === 'function' ? bottomGap() : bottomGap)
      const naturalTop = anchor.getBoundingClientRect().top
      revealOffset = computeRevealOffset(revealOffset, delta, overflow, naturalTop, top)
      element.style.top = `${String(top - revealOffset)}px`
    }

    const plan = () => {
      if (frame !== 0) return
      frame = requestAnimationFrame(apply)
    }

    window.addEventListener('scroll', plan, { passive: true })
    window.addEventListener('resize', plan)
    // De compacte kop verschijnt pas na het scrollen (een waarnemer, `CompactHeader`), en dan verandert
    // de lucht erboven zonder dat er nog een scrollstap komt.
    document.addEventListener(STICKY_TOP_CHANGED, plan)

    // Initiële positionering: scroll/resize vuren niet vanzelf wanneer de
    // inhoudsopgave ná mount verschijnt (de tweede sectie komt pas later binnen).
    // Poll begrensd tot beide refs bestaan; blijft de inhoudsopgave onder de twee
    // secties, dan stopt de poll vanzelf zonder ooit iets te hebben toegepast.
    let attempts = 0
    const ensureFirstApply = () => {
      waitFrame = 0
      if (elementRef.current !== null && anchorRef.current !== null) {
        plan()
        return
      }
      if (attempts++ < 120) waitFrame = requestAnimationFrame(ensureFirstApply)
    }
    ensureFirstApply()

    return () => {
      window.removeEventListener('scroll', plan)
      window.removeEventListener('resize', plan)
      document.removeEventListener(STICKY_TOP_CHANGED, plan)
      observer.disconnect()
      if (frame !== 0) cancelAnimationFrame(frame)
      if (waitFrame !== 0) cancelAnimationFrame(waitFrame)
      // Ruim de inline-top op van het element dat we daadwerkelijk gestyled hebben.
      if (observed !== null) observed.style.top = ''
    }
  }, [elementRef, anchorRef, topGap, bottomGap])
}
